import { createElement, useMemo, type ReactNode, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider as AppThemeProvider, useTheme as useAppTheme } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { InstagramAnalysisProvider } from "@/contexts/InstagramAnalysisContext";
import SessionTracker from "@/components/SessionTracker";
import SeoMeta from "@/components/SeoMeta";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import LoadingScreen from "@/components/LoadingScreen";
import AuthProcessing from "./pages/AuthProcessing";
const Index = lazy(() => import("./pages/Index"));
const MobileHome = lazy(() => import("./pages/MobileHome"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const HistoryDetail = lazy(() => import("./pages/HistoryDetail"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const LogoMeaningPost = lazy(() => import("./pages/blog/LogoMeaningPost"));
import { buildMuiTheme } from "@/theme/muiTheme";

const queryClient = new QueryClient();

const shouldLoadVercelScripts =
  (import.meta as ImportMeta).env?.PROD ||
  (import.meta as ImportMeta).env?.VITE_ENABLE_VERCEL_INSIGHTS === "true";

const MuiThemeBridge = ({ children }: { children: ReactNode }) => {
  const { actualTheme } = useAppTheme();
  const muiTheme = useMemo(() => buildMuiTheme(actualTheme), [actualTheme]);

  return createElement(
    MuiThemeProvider,
    { theme: muiTheme },
    createElement(CssBaseline, null),
    children
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppThemeProvider>
      <MuiThemeBridge>
        <LanguageProvider>
          <AuthProvider>
            <InstagramAnalysisProvider>
              <HelmetProvider>
                <SeoMeta />
                <SessionTracker />
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  {shouldLoadVercelScripts && (
                    <>
                      <SpeedInsights />
                      <Analytics />
                    </>
                  )}
                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true
                    }}
                  >
                    <AnalyticsRouteTracker />
                    <Suspense fallback={<LoadingScreen />}>
                      <Routes>
                      <Route path="/" element={<Navigate to="/en/" replace />} />
                      <Route path="/demo" element={<Navigate to="/en/demo/" replace />} />

                      <Route path="/en" element={<Index />} />
                      <Route path="/es" element={<Index />} />
                      <Route path="/pt" element={<Index />} />
                      <Route path="/history" element={<Navigate to="/en/history/" replace />} />
                      <Route path="/en/history" element={<Index />} />
                      <Route path="/es/history" element={<Index />} />
                      <Route path="/pt/history" element={<Index />} />

                      <Route path="/history/:id" element={<HistoryDetail />} />
                      <Route path="/en/history/:id" element={<HistoryDetail />} />
                      <Route path="/es/history/:id" element={<HistoryDetail />} />
                      <Route path="/pt/history/:id" element={<HistoryDetail />} />
                      <Route path="/en/demo" element={<Index />} />
                      <Route path="/es/demo" element={<Index />} />
                      <Route path="/pt/demo" element={<Index />} />

                      <Route path="/mobile" element={<Navigate to="/en/mobile/" replace />} />
                      <Route path="/en/mobile" element={<MobileHome />} />
                      <Route path="/es/mobile" element={<MobileHome />} />
                      <Route path="/pt/mobile" element={<MobileHome />} />

                      <Route path="/terms" element={<Navigate to="/en/terms/" replace />} />
                      <Route path="/privacy" element={<Navigate to="/en/privacy/" replace />} />

                      <Route path="/en/terms" element={<Terms />} />
                      <Route path="/es/terms" element={<Terms />} />
                      <Route path="/pt/terms" element={<Terms />} />

                      <Route path="/en/privacy" element={<Privacy />} />
                      <Route path="/es/privacy" element={<Privacy />} />
                      <Route path="/pt/privacy" element={<Privacy />} />

                        <Route path="/blog" element={<Navigate to="/en/blog/" replace />} />
                        <Route path="/:lang/blog" element={<BlogIndex />} />
                        <Route path="/:lang/blog/:slug" element={<LogoMeaningPost />} />

                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/auth/processing" element={<AuthProcessing />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </BrowserRouter>
                </TooltipProvider>
              </HelmetProvider>
            </InstagramAnalysisProvider>
          </AuthProvider>
        </LanguageProvider>
      </MuiThemeBridge>
    </AppThemeProvider>
  </QueryClientProvider>
);

export default App;
